import { baseCleanProps } from "common/api/Api";
import { fetcher, pricelistFetcher, IGlobalRequestOptions } from "common/api/Fetcher";
import { DocumentDisplayType, DocumentRecord, DocumentableType, IDocumentData } from "common/records/Document";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { DocumentParamsRecord } from "common/records/index/DocumentParams";
import { toParams } from "common/records/index/QueryParamsBase";
import { Nullable } from "common/types";
import { List } from "immutable";

export interface IDocumentIndexResponse {
  documents: IDocumentData[];
  meta: { pagination: IMetaPaginationData };
}

export interface IDocumentResponse {
  document: IDocumentData;
}

export interface IDocumentOptions {
  documentableId: number;
  documentableType: DocumentableType;
  display?: DocumentDisplayType;
  folder_id?: Nullable<number>;
  sort_order?: number;
  autoImportMeasurements?: boolean;
  /** Staging records to drop from `documents.byId` on upload success, paired positionally with `files`. */
  replacedNegativeIds?: number[];
}

export interface IDirectUploadResponse {
  urls: {
    id: string;
    url: string;
  }[];
}

const urls = {
  Org: "api/v1/orgs/:id/documents",
  Job: "api/v1/jobs/:id/documents",
  EstimateLineItem: "api/v1/estimate_line_items/:id/documents",
  Product: "api/v1/products/:id/documents",
  EstimateTemplate: "api/v1/estimate_templates/:id/documents",
};

export const list = (queryParams: DocumentParamsRecord): Promise<IDocumentIndexResponse> => {
  const parameters = toParams(queryParams);
  delete parameters.documentableId;
  delete parameters.documentableType;

  const actionUrl = urls[queryParams.documentableType].replace(":id", queryParams.documentableId.toString());

  return getFetcher(queryParams.documentableType).get<IDocumentIndexResponse>(actionUrl, parameters);
};

export const create = (document: DocumentRecord): Promise<IDocumentResponse> => {
  const actionUrl = urlSetup(document);
  return getFetcher(document.documentable_type).post(actionUrl, cleanProps(document));
};

export const update = (document: DocumentRecord, options?: IGlobalRequestOptions): Promise<IDocumentResponse> => {
  const f = getFetcher(document.documentable_type);
  const actionUrl = f.joinUrls(urlSetup(document), document.id.toString());
  return options ? f.patch(actionUrl, cleanProps(document), options) : f.patch(actionUrl, cleanProps(document));
};

export const destroy = (document: DocumentRecord, options?: IGlobalRequestOptions): Promise<void> => {
  const f = getFetcher(document.documentable_type);
  const actionUrl = f.joinUrls(urlSetup(document), document.id.toString());
  return options ? f.delete(actionUrl, {}, options) : f.delete(actionUrl);
};

export const directUpload = (
  fileCount: number,
  documentableType: DocumentableType,
  documentableId: number,
): Promise<IDirectUploadResponse> => {
  const f = getFetcher(documentableType);
  const actionUrl = f.joinUrls(urls[documentableType].replace(":id", documentableId.toString()), "direct");
  return f.post(actionUrl, { count: fileCount });
};

export const importMeasurement = (
  document: DocumentRecord,
  options?: IGlobalRequestOptions,
): Promise<IDocumentResponse> => {
  const f = getFetcher(document.documentable_type);
  const actionUrl = f.joinUrls(urlSetup(document), document.id.toString(), "import");
  return options ? f.post(actionUrl, cleanProps(document), options) : f.post(actionUrl, cleanProps(document));
};

export const combineDocuments = (
  documentIds: List<number>,
  name: string,
  jobId: number,
  options?: IGlobalRequestOptions,
): Promise<IDocumentResponse> => {
  const actionUrl = urls.Job.replace(":id", jobId.toString()).concat("/combine");
  return options
    ? fetcher.post(actionUrl, { document_ids: documentIds, name: name }, options)
    : fetcher.post(actionUrl, { document_ids: documentIds, name: name });
};

export const urlSetup = (document: DocumentRecord): string => {
  return urls[document.documentable_type || "Org"].replace(":id", document.documentable_id?.toString() || "");
};

export const getFetcher = (type: Nullable<DocumentableType>) => {
  if (type === "Product") {
    return pricelistFetcher();
  }
  return fetcher;
};

export const cleanProps = (record: DocumentRecord): any => {
  const data = record.toJS() as IDocumentData;

  if (data.file_queue) delete data.file_queue;

  return baseCleanProps(data);
};
